import React, { useEffect, useState } from 'react';
import Routers from '@/router/main';
import { useUserStore } from '@/stores/user/userStore';
import { updateUserDataLayer } from '@/helpers/analytics';
import { useTimezone } from '@/hooks/useTimezone';
import { useTimezoneStore } from '@/stores/timezone/timezoneStore';
import { identifyUser } from '@/utils/clarity';
import mixpanel from '@/utils/mixpanel';
import { useABVariantSync } from '@/hooks/useABVariantSync';
import { useUserStoreCrossTabSync } from '@/hooks/useUserStoreCrossTabSync';
import { getABVariant } from '@/utils/abVariant';

const hardcodedPassword = 'Dante@111';
const passwordKey = 'lastAuthTime';
const expires = 2 * 60 * 60 * 1000; // 2 hours

const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checkedAuth, setCheckedAuth] = useState(false);
  const [inputPassword, setInputPassword] = useState('');
  const { user, auth } = useUserStore();
  useUserStoreCrossTabSync();
  const { timezone, offset } = useTimezone();
  const setTimezoneInfo = useTimezoneStore((state) => state.setTimezoneInfo);

  // A/B test: only users created in the last 14 days (>= March 6) participate.
  // Older users are locked to variant A.
  const AB_TEST_CUTOFF = '2026-03-06';
  const isNewUser = auth?.date_created && auth.date_created >= AB_TEST_CUTOFF;

  // Phase 2: Reconcile A/B variant with real user_id after login (new users only)
  useABVariantSync(isNewUser ? user?.id : undefined);

  useEffect(() => {
    if (!auth?.date_created || !user?.id) return;
    if (isNewUser) return; // New users keep their Bucketeer assignment

    // Existing user - force variant A
    const current = document.cookie.match(/(?:^|;\s*)bkt_variant=([^;]*)/)?.[1];
    if (current !== 'a') {
      document.cookie = 'bkt_variant=a; path=/; max-age=2592000; secure; samesite=lax';
      document.cookie = `bkt_user_id=${encodeURIComponent(user.id)}; path=/; max-age=2592000; secure; samesite=lax`;
      if (current && current !== 'a') {
        // Cache-busting navigation to ensure the browser fetches fresh HTML
        // from the correct variant origin (prevents stale cached assets)
        window.location.href = window.location.pathname + '?_ab=' + Date.now();
      }
    }
  }, [auth?.date_created, user?.id, isNewUser]);

  const checkAuthentication = () => {
    const lastAuthTime = localStorage.getItem(passwordKey);
    if (!lastAuthTime) return false;
    const currentTime = new Date().getTime();
    return currentTime - parseInt(lastAuthTime, 10) < expires;
  };

  // Set timezone info as soon as it's available
  useEffect(() => {
    setTimezoneInfo({ timezone, offset });
  }, [timezone, offset, setTimezoneInfo]);

  useEffect(() => {
    if (
      window.location.href.includes('https://app.dante-ai.com/') ||
      window.location.href.includes('//localhost') ||
      window.location.href.includes('https://thedanteai-dante-fronte-20ec6f3a-abc6k.ondigitalocean.app/')
    ) {
      // On the official domain: skip password
      setIsAuthenticated(true);
      setCheckedAuth(true);
    } else {
      // Anywhere else: do the password check
      const alreadyAuthenticated = checkAuthentication();
      setIsAuthenticated(alreadyAuthenticated);
      setCheckedAuth(true);
    }
  }, []);

  useEffect(() => {
    // Update dataLayer with user information when auth changes
    // Only send user details if we have a valid access token
    updateUserDataLayer(user, auth);
  }, [user, auth]);

  useEffect(() => {
    if (user && user.email) {
      // Identify user in Microsoft Clarity
      identifyUser(
        user.email, // customId
        null, // customSessionId
        null, // customPageId
        user.name || user.full_name // friendlyName
      );
    }
  }, [user]);

  useEffect(() => {
    if (user && user.id) {
      // Identify user in Mixpanel
      mixpanel.identify(user.id);

      // Set user properties
      mixpanel.people.set({
        '$email': user.email || '',
        '$name': user.full_name || user.name || '',
        ab_test_variant: getABVariant(),
      });
    }
  }, [user]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (inputPassword === hardcodedPassword) {
      localStorage.setItem(passwordKey, new Date().getTime().toString());
      setIsAuthenticated(true);
    } else {
      alert('Incorrect password. Redirecting...');
      window.location.href = 'https://www.google.com/';
    }
  };

  // If we haven't finished checking authentication status, don't render anything
  if (!checkedAuth) {
    return null; // or a loading spinner if you'd like
  }

  // If not authenticated, display the password form
  if (!isAuthenticated) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          fontFamily: 'Arial, sans-serif',
        }}
      >
        <h2>Please Enter the Password</h2>
        <form
          onSubmit={handleSubmit}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            width: '200px',
          }}
        >
          <input
            type="password"
            value={inputPassword}
            onChange={(e) => setInputPassword(e.target.value)}
            placeholder="Password"
            style={{ padding: '8px', fontSize: '16px' }}
          />
          <button
            type="submit"
            style={{ padding: '8px', fontSize: '14px', cursor: 'pointer' }}
          >
            Submit
          </button>
        </form>
      </div>
    );
  }

  // If authenticated, show the main app
  return <Routers />;
};

export default App;
